import { DayPicker, getDefaultClassNames, type DayPickerProps } from 'react-day-picker';

import { getButtonClassName, type ButtonProps } from '@/components/buttons';
import { cn } from '@/lib/utils';

import CalendarChevron from './CalendarChevron';
import CalendarDayButton from './CalendarDayButton';
import CalendarRoot from './CalendarRoot';
import CalendarWeekNumber from './CalendarWeekNumber';

export type CalendarProps = DayPickerProps & {
  buttonVariant?: ButtonProps['variant'];
};

const Calendar = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  locale,
  formatters,
  components,
  ...props
}: CalendarProps) => {
  const defaultClassNames = getDefaultClassNames();
  const navigationButtonClassName = getButtonClassName({
    variant: buttonVariant,
    size: 'icon',
  }).className;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'group/calendar bg-white [--cell-size:--spacing(9)] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-4 md:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-4 p-2', defaultClassNames.month),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 border-b border-gray-200 p-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          navigationButtonClassName,
          'p-0 select-none aria-disabled:bg-transparent! aria-disabled:text-gray-400!',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          navigationButtonClassName,
          'p-0 select-none aria-disabled:bg-transparent! aria-disabled:text-gray-400!',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'flex h-6 w-full items-center justify-center px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'flex h-6 w-full items-center justify-center gap-1.5 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn('cn-calendar-dropdown-root relative', defaultClassNames.dropdown_root),
        dropdown: cn('absolute inset-0 bg-white opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label'
            ? 'text-sm'
            : 'cn-calendar-caption-label flex items-center gap-1 text-sm [&>svg]:size-3.5 [&>svg]:text-gray-600',
          defaultClassNames.caption_label,
        ),
        month_grid: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn(
          'flex-1 text-sm font-normal text-gray-600 select-none',
          defaultClassNames.weekday,
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
        week_number: cn('text-[0.8rem] text-gray-600 select-none', defaultClassNames.week_number),
        day: cn(
          'group/day relative aspect-square size-full p-0 text-center select-none',
          defaultClassNames.day,
        ),
        range_start: cn(
          'relative isolate z-0 rounded-l-full bg-green-400/30',
          defaultClassNames.range_start,
        ),
        range_middle: cn('rounded-none bg-green-400/30', defaultClassNames.range_middle),
        range_end: cn(
          'relative isolate z-0 rounded-r-full bg-green-400/30',
          defaultClassNames.range_end,
        ),
        today: cn(
          'relative after:absolute after:-inset-0.5 after:rounded-xs after:border-1.5 after:border-green-500',
          defaultClassNames.today,
        ),
        outside: cn('*:data-[slot=button]:text-gray-600', defaultClassNames.outside),
        disabled: cn(
          '*:data-[slot=button]:bg-transparent! *:data-[slot=button]:text-gray-400!',
          defaultClassNames.disabled,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        DayButton: (dayButtonProps) => <CalendarDayButton locale={locale} {...dayButtonProps} />,
        WeekNumber: CalendarWeekNumber,
        ...components,
      }}
      {...props}
    />
  );
};

export default Calendar;
